/**
 * RedactionPlanResults — Carte finalisée d'un job de rédaction. Apparaît dans
 * la timeline chat APRÈS la fin du job (mute via `applyRedactionPlanResults`).
 *
 * Affiche le verdict (succès / refus / interruption / erreur) + un bouton « Ouvrir »
 * (style Codex) pour aller voir le résultat dans le canvas. INDÉPENDANT du
 * tool_result rendu au LLM (qui pilote la réponse conversationnelle de l'agent
 * dans la timeline juste en-dessous de cette carte).
 *
 * Quand un `progressSnapshot` est présent (capturé au flip progress → results,
 * 2026-07-13), le bandeau devient dépliable : le détail complet de la
 * génération (arguments, recherches JP, synthèse, insertion) se re-consulte
 * via `RedactionPlanProgress` en mode `embedded`, phase `complete`.
 */

import * as React from 'react';
import { Check, XCircle, AlertCircle, PenLine, Scale, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TimelineItem } from '../../types/session.types';
import { RedactionPlanProgress } from './RedactionPlanProgress';
import { productionRedigee, type ProductionRedigee } from './productionRedigee';

type Verdict = 'completed' | 'declined' | 'interrupted' | 'cancelled' | 'error';

type ProgressSnapshot = Extract<TimelineItem, { type: 'redaction_plan_results' }>['progressSnapshot'];

interface RedactionPlanResultsProps {
  requestId: string;
  jobId: string;
  kind: 'research' | 'generation';
  verdict: Verdict;
  proposedPayload: {
    nom_acte?: string | null;
    specialisation?: string | null;
    generation_mode?: 'full' | 'section_i' | 'section_ii_iii';
    /**
     * Lancement en un seul temps : `kind` reste `'research'` alors que le job a
     * produit l'acte. Sans ce champ la carte annonçait « Plan rédigé » sous un
     * acte entier. Voir `productionRedigee`.
     */
    full_document?: boolean;
  };
  extras?: Record<string, unknown>;
  /** Vrai si le canvas est actuellement ouvert (pilote le libellé Ouvrir/Fermer). */
  canvasOpen?: boolean;
  /** Bascule l'ouverture du canvas latéral (bouton « Ouvrir »/« Fermer »). Absent → pas de bouton. */
  onToggleCanvas?: () => void;
  /** Détail de la génération, dépliable après le terminal. */
  progressSnapshot?: ProgressSnapshot;
}

interface VerdictDescriptor {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  subtitle: string;
}

function describeVerdict(production: ProductionRedigee, verdict: Verdict): VerdictDescriptor {
  switch (verdict) {
    case 'completed':
      return {
        // Check CLASSIQUE (neutre, pas vert) — demande produit 2026-07-21.
        icon: <Check className="h-3.5 w-3.5" />,
        iconBg: 'bg-stone-100',
        iconColor: 'text-stone-600',
        title: production === 'plan' ? 'Plan rédigé' : 'Acte rédigé',
        // Sous-titre « disponible dans le canvas latéral » retiré (bruit) : le bouton « Ouvrir » suffit.
        subtitle: '',
      };
    case 'declined':
      return {
        icon: <XCircle className="h-3.5 w-3.5" />,
        iconBg: 'bg-stone-100',
        iconColor: 'text-stone-700',
        title: 'Rédaction annulée',
        subtitle: 'Tu as choisi de ne pas lancer ce job.',
      };
    case 'cancelled':
      // Séparé de `declined` depuis #861 : ce verdict ne signifiait qu'un refus
      // AVANT lancement, il signifie aussi une écriture arrêtée en cours de
      // route. Dire « tu as choisi de ne pas lancer » à un avocat dont le job a
      // tourné — et coûté — serait faux.
      return {
        icon: <XCircle className="h-3.5 w-3.5" />,
        iconBg: 'bg-stone-100',
        iconColor: 'text-stone-700',
        title: 'Rédaction arrêtée',
        subtitle: 'L\'écriture s\'est arrêtée avant la fin. Ce qui avait été écrit est conservé.',
      };
    case 'interrupted':
      return {
        icon: <XCircle className="h-3.5 w-3.5" />,
        iconBg: 'bg-stone-100',
        iconColor: 'text-stone-700',
        title: 'Rédaction interrompue',
        subtitle: 'Le job a été stoppé.',
      };
    case 'error':
    default:
      return {
        icon: <AlertCircle className="h-3.5 w-3.5" />,
        iconBg: 'bg-red-100',
        iconColor: 'text-red-700',
        title: 'Erreur lors du job',
        subtitle: 'Le job n\'a pas pu aboutir. L\'agent peut reformuler ou retenter.',
      };
  }
}

/** Icône de verdict (pastille colorée). */
function VerdictIcon({ d }: { d: VerdictDescriptor }) {
  return <div className={cn('rounded-md p-1.5 shrink-0', d.iconBg, d.iconColor)}>{d.icon}</div>;
}

/** Titre + sous-titre du verdict. Sous-titre rendu seulement s'il est non vide. */
function VerdictText({
  d,
  production,
  nomActe,
}: {
  d: VerdictDescriptor;
  production: ProductionRedigee;
  nomActe?: string | null;
}) {
  return (
    <div className="flex-1 min-w-0 text-left">
      <p className="text-sm font-medium text-stone-900 leading-tight flex items-center gap-1.5">
        {/* La balance annonce un plan de recherche, la plume un acte écrit. En un
            seul temps le job cherche ET rédige : c'est la plume qui dit vrai,
            puisque c'est l'acte que l'avocat trouvera dans le canvas. */}
        {production === 'plan' ? (
          <Scale className="h-3 w-3 text-stone-400 shrink-0" />
        ) : (
          <PenLine className="h-3 w-3 text-stone-400 shrink-0" />
        )}
        <span className="min-w-0 truncate">
          {d.title}
          {nomActe ? <span className="text-stone-500 font-normal"> · {nomActe}</span> : null}
        </span>
      </p>
      {d.subtitle && <p className="text-xs text-stone-500 mt-0.5">{d.subtitle}</p>}
    </div>
  );
}

export function RedactionPlanResults({
  requestId,
  jobId,
  kind,
  verdict,
  proposedPayload,
  canvasOpen = false,
  onToggleCanvas,
  progressSnapshot,
}: RedactionPlanResultsProps) {
  // Ce que le job a produit, pas quel job a tourné (cf. `productionRedigee`).
  const production = productionRedigee(kind, proposedPayload.full_document);
  const d = describeVerdict(production, verdict);
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  const hasDetails = Boolean(
    progressSnapshot &&
    (progressSnapshot.argumentTitles?.length || progressSnapshot.searchQueries?.length),
  );
  // « Ouvrir » ne dépend plus du seul succès (#861). Depuis #858, une écriture
  // interrompue LAISSE dans le canvas ce qu'elle avait déjà produit, et l'agent
  // est explicitement chargé d'en parler — masquer le bouton rendrait ce
  // fragment inatteignable en un clic, au moment précis où l'avocat veut voir
  // ce qui a survécu. Restent exclus les verdicts d'AVANT lancement, où le
  // canvas ne contient rien de ce job.
  const showToggle = verdict !== 'declined' && !!onToggleCanvas;

  return (
    <div className="my-3 rounded-xl border border-stone-200 bg-white shadow-soft overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3">
        {/* Zone gauche : icône + titre. Cliquable pour déplier quand il y a un détail
            (bouton dédié, sibling du bouton « Ouvrir » — pas d'imbrication de boutons). */}
        {hasDetails ? (
          <button
            type="button"
            onClick={() => setDetailsOpen((open) => !open)}
            className="flex items-center gap-2 flex-1 min-w-0 text-left rounded-md -mx-1 px-1 py-0.5 hover:bg-stone-50 transition-colors"
            aria-expanded={detailsOpen}
            aria-label={detailsOpen ? 'Replier le détail de la génération' : 'Déplier le détail de la génération'}
          >
            <VerdictIcon d={d} />
            <VerdictText d={d} production={production} nomActe={proposedPayload.nom_acte} />
            <ChevronRight
              className={cn('w-4 h-4 text-stone-400 shrink-0 transition-transform', detailsOpen && 'rotate-90')}
            />
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <VerdictIcon d={d} />
            <VerdictText d={d} production={production} nomActe={proposedPayload.nom_acte} />
          </div>
        )}

        {/* Bouton « Ouvrir »/« Fermer » au bout (style Codex) — bascule le canvas latéral. */}
        {showToggle && (
          <button
            type="button"
            onClick={onToggleCanvas}
            className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 transition-colors"
          >
            {canvasOpen ? 'Fermer' : 'Ouvrir'}
          </button>
        )}
      </div>

      {hasDetails && detailsOpen && progressSnapshot && (
        <div className="border-t border-stone-100">
          <RedactionPlanProgress
            embedded
            requestId={requestId}
            jobId={jobId}
            kind={kind}
            phase="complete"
            argumentTitles={progressSnapshot.argumentTitles}
            completedQueries={progressSnapshot.completedQueries}
            totalQueries={progressSnapshot.totalQueries}
            searchQueries={progressSnapshot.searchQueries}
            legalVerification={progressSnapshot.legalVerification}
            skipJurisprudence={progressSnapshot.skipJurisprudence}
            // Sans lui, déplier le détail d'un acte complet rouvrait un déroulé
            // qui parle de plan — le même mensonge, une fois replié.
            fullDocument={proposedPayload.full_document === true}
            proposedPayload={proposedPayload}
          />
        </div>
      )}
    </div>
  );
}
